import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { CarCard } from "./CarCard";
import { Eyebrow, Reveal } from "./Reveal";
import { InventoryFilters } from "./InventoryFilters";
import { fetchCars, waLink } from "../lib/company";
import { useT } from "../lib/i18n";

const SORTS = ["recenti", "prezzo-asc", "prezzo-desc"];
const NO_FILTERS = { fuel: "", price: null, year: "" };

export const Inventory = () => {
  const t = useT();
  const [cars, setCars] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recenti");
  const [filters, setFilters] = useState(NO_FILTERS);
  useEffect(() => { fetchCars().then(setCars); }, []);
  const active = !!(q.trim() || filters.fuel || filters.price || filters.year);
  const reset = () => { setFilters(NO_FILTERS); setQ(""); };

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const [pmin, pmax] = filters.price ?? [-Infinity, Infinity];
    let l = (cars ?? []).filter((c) =>
      (!s || [c.marca, c.modello, c.colore, c.carburante, c.anno].filter(Boolean).join(" ").toLowerCase().includes(s)) &&
      (!filters.fuel || c.carburante?.trim().toLowerCase() === filters.fuel) &&
      c.prezzo >= pmin && c.prezzo <= pmax &&
      (!filters.year || (c.anno && c.anno >= Number(filters.year))));
    if (sort === "prezzo-asc") l = [...l].sort((a, b) => a.prezzo - b.prezzo);
    if (sort === "prezzo-desc") l = [...l].sort((a, b) => b.prezzo - a.prezzo);
    return l;
  }, [cars, q, sort, filters]);

  return (
    <section id="auto" className="py-20 md:py-32" data-testid="inventory-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 md:mb-16">
          <Reveal>
            <Eyebrow>{t.inv.eyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[0.95]">
              {t.inv.t1} <span className="font-serif-i font-normal text-[var(--red)]">{t.inv.em}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <label className="flex items-center gap-2 rounded-full border border-[var(--line)] bg-white px-5 h-12 sm:w-72">
              <Search className="w-4 h-4 text-[var(--muted)]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.inv.search} data-testid="inventory-search-input"
                className="bg-transparent outline-none text-sm w-full" />
            </label>
            <div className="flex rounded-full border border-[var(--line)] bg-white p-1 h-12 overflow-x-auto">
              {SORTS.map((k) => (
                <button key={k} onClick={() => setSort(k)} data-testid={`inventory-sort-${k}`}
                  className={`rounded-full px-4 text-xs font-semibold whitespace-nowrap transition-colors ${sort === k ? "bg-[var(--ink)] text-white" : "text-[var(--ink-2)] hover:text-[var(--red)]"}`}>{t.inv.sorts[k]}</button>
              ))}
            </div>
          </Reveal>
        </div>

        {cars?.length > 0 && (
          <InventoryFilters cars={cars} filters={filters} setFilters={setFilters} count={list.length} active={active} onReset={reset} />
        )}

        {cars === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] rounded-[22px] bg-[#E9E6DE] animate-pulse" />)}
          </div>
        ) : list.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="inventory-grid">
            {list.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
          </div>
        ) : (
          <div className="rounded-[28px] border border-dashed border-[var(--line)] bg-white/60 p-10 md:p-16 text-center" data-testid="inventory-empty">
            <p className="font-display text-2xl md:text-3xl font-semibold">{cars.length ? t.inv.emptyNoMatch : t.inv.emptyNone}</p>
            <p className="mt-3 text-[var(--muted)]">{t.inv.emptySub}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {active && <button onClick={reset} data-testid="inventory-empty-reset" className="rounded-full border border-[var(--ink)] px-7 py-3.5 text-sm font-semibold hover:bg-[var(--ink)] hover:text-white transition-colors">{t.inv.reset}</button>}
              <a href={waLink(t.inv.emptyMsg)} target="_blank" rel="noreferrer" data-testid="inventory-empty-whatsapp"
                className="inline-flex rounded-full bg-[var(--ink)] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[var(--red)] transition-colors">{t.inv.emptyCta}</a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
